import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Hash, ArrowLeft, LogIn } from 'lucide-react';
import { loginStudent, checkRateLimit } from '../../services/authService';
import AuthLayout from '../ui/AuthLayout';
import GlassCard from '../ui/GlassCard';
import Input from '../ui/Input';
import Button from '../ui/Button';

/**
 * Student Login Screen
 * Redesigned with new component system.
 */
const StudentLogin = ({ onSuccess, onNavigate }) => {
  const [formData, setFormData] = useState({
    classCode: '',
    username: '',
    pin: ''
  });

  const [rememberMe, setRememberMe] = useState(true);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.classCode.trim()) {
      setError('Please enter your class code');
      return;
    }
    if (!formData.username.trim()) {
      setError('Please enter your username');
      return;
    }
    if (!/^\d{4}$/.test(formData.pin)) {
      setError('PIN must be 4 digits');
      return;
    }

    setLogging(true);

    try {
      // Check rate limiting
      const rateLimitCheck = await checkRateLimit(formData.username, formData.classCode);
      if (!rateLimitCheck.allowed) {
        setError(rateLimitCheck.message);
        setLogging(false);
        return;
      }

      // Attempt login
      const result = await loginStudent(
        formData.classCode.toUpperCase(),
        formData.username.toLowerCase(),
        formData.pin
      );

      if (result.success) {
        if (onSuccess) onSuccess(result.student, result.session);
      } else {
        setError(result.error || 'Login failed');
        setFormData(prev => ({ ...prev, pin: '' }));
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLogging(false);
    }
  };

  return (
    <AuthLayout
      title="Student Login"
      subtitle="Welcome back to class!"
    >
      <div className="max-w-md mx-auto">
        <GlassCard>
          {/* Back Button */}
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            className="absolute top-6 left-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <Input
              label="Class Code"
              name="classCode"
              value={formData.classCode}
              onChange={handleChange}
              placeholder="FLUENCE-CLASS6-2025"
              icon={Hash}
              required
            />

            <Input
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="anaya01"
              icon={User}
              required
            />

            <Input
              label="PIN"
              name="pin"
              type="password"
              value={formData.pin}
              onChange={handleChange}
              placeholder="1234"
              icon={Lock}
              required
            />

            {/* Remember Me & Error */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-red-500 text-sm font-medium text-center"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              variant="success"
              loading={logging}
              className="w-full"
              icon={LogIn}
            >
              Login
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Don't have an account?</p>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => onNavigate && onNavigate('student-register')}
            >
              Join a Class
            </Button>
          </div>
        </GlassCard>
      </div>
    </AuthLayout>
  );
};

export default StudentLogin;
